/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockItem } from "@app/vue/pages/Inventory/subpages/Stock/types";
import { lineCost, unitOptions, type EditableLine } from "./recipeCost";

type Props = {
  line: EditableLine;
  stockItems: StockItem[];
  yieldPortions: number;
  formatMoney: (value: number) => string;
  onChange: (line: EditableLine) => void;
  onRemove: () => void;
};

export const RecipeLineRow = defineComponent<Props>({
  name: "RecipeLineRow",
  props: ["line", "stockItems", "yieldPortions", "formatMoney", "onChange", "onRemove"],
  setup(props) {
    const { t } = useLocalization();

    const selectItem = (id: number) => {
      const item = props.stockItems.find((s) => s.id === id);
      props.onChange({ ...props.line, stock_item_id: id, unit: item?.base_unit ?? "" });
    };

    return () => {
      const item = props.stockItems.find((s) => s.id === props.line.stock_item_id);
      const { baseQtyPerPortion, cost } = lineCost(props.line, item, props.yieldPortions);

      return (
        <tr>
          <td>
            <select
              class="select select-bordered select-sm w-full min-w-40"
              value={props.line.stock_item_id ?? ""}
              onChange={(e: Event) => selectItem(Number((e.target as HTMLSelectElement).value))}
            >
              <option value="" disabled>
                {t("inventory.recipe.pick_item")}
              </option>
              {props.stockItems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </td>
          <td>
            <input
              type="number"
              min="0"
              step="any"
              class="input input-bordered input-sm w-20"
              value={props.line.net_qty || ""}
              onInput={(e: Event) =>
                props.onChange({
                  ...props.line,
                  net_qty: Number((e.target as HTMLInputElement).value),
                })
              }
            />
          </td>
          <td>
            <select
              class="select select-bordered select-sm w-24"
              value={props.line.unit}
              disabled={!item}
              onChange={(e: Event) =>
                props.onChange({ ...props.line, unit: (e.target as HTMLSelectElement).value })
              }
            >
              {item && unitOptions(item).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </td>
          <td>
            <input
              type="number"
              min="0"
              max="99"
              step="any"
              class="input input-bordered input-sm w-16"
              value={props.line.loss_pct || ""}
              placeholder="0"
              onInput={(e: Event) =>
                props.onChange({
                  ...props.line,
                  loss_pct: Number((e.target as HTMLInputElement).value),
                })
              }
            />
          </td>
          <td class="whitespace-nowrap text-right">
            <div class="text-sm font-medium">
              {cost == null ? "—" : props.formatMoney(cost)}
            </div>
            {baseQtyPerPortion != null && item && (
              <div class="text-xs text-base-content/50">
                {`${Number(baseQtyPerPortion.toFixed(2))} ${item.base_unit}`}
              </div>
            )}
          </td>
          <td>
            <button
              class="btn btn-ghost btn-xs btn-square"
              aria-label={t("inventory.recipe.remove_line")}
              onClick={() => props.onRemove()}
            >
              <span class="material-symbols-outlined text-base">close</span>
            </button>
          </td>
        </tr>
      );
    };
  },
});
